// PublicHeader.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Navbar, Dropdown, Button, Avatar } from 'flowbite-react';
import { HiOutlineGlobe, HiOutlineUserCircle, HiOutlineMenuAlt3, HiLogin, HiLogout } from 'react-icons/hi';
import { useAuth } from '../../contexts/AuthContext';
import { getUserMenuHeaderLines } from '../../utils/userMenuHeader';

const PublicHeader = () => {
  const { user, loading: authLoading, signOut } = useAuth();
  const [_isMenuOpen, _setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // State for New Year countdown timer
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [promoExpired, setPromoExpired] = useState(false);

  // New Year countdown timer - midnight LA time (Pacific)
  useEffect(() => {
    const calculateCountdown = () => {
      // Target: January 1, 2026 00:00:00 Pacific Time
      const targetDate = new Date('2026-01-01T00:00:00-08:00');
      const now = new Date();
      const difference = targetDate - now;

      if (difference <= 0) {
        setPromoExpired(true);
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      return { days, hours, minutes, seconds };
    };

    // If target date has already passed, mark as expired immediately without starting the interval
    const targetDate = new Date('2026-01-01T00:00:00-08:00');
    if (new Date() >= targetDate) {
      setPromoExpired(true);
      return;
    }

    setCountdown(calculateCountdown());

    const interval = setInterval(() => {
      setCountdown(calculateCountdown());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Hide user menu on payment page to keep focus on completing payment
  const isPaymentPage = location.pathname === '/newuser/payment';

  // Extract user data with same logic as Navbar
  const userData = user?.user || user;
  const hasUserData = userData && (userData.name || userData.email);
  const userMenuHeader = getUserMenuHeaderLines(userData, hasUserData);

  // Determine which avatar to display: Google profile picture or emoji
  const getEffectiveAvatar = () => {
    if (!userData) return null;

    // Use effectiveAvatar from API if available, otherwise determine locally
    if (userData.effectiveAvatar && userData.avatarType) {
      return {
        avatar: userData.effectiveAvatar,
        type: userData.avatarType
      };
    }

    // Fallback logic for older API responses
    const metadata = userData.metadata || {};
    const googleProfilePicture = metadata.picture;
    const avatarPreference = metadata.avatarPreference;

    // If user has Google profile picture and hasn't explicitly chosen emoji
    if (googleProfilePicture && avatarPreference !== 'emoji') {
      return {
        avatar: googleProfilePicture,
        type: 'google'
      };
    }

    return {
      avatar: userData.avatar || '😀',
      type: 'emoji'
    };
  };

  const effectiveAvatarData = getEffectiveAvatar();

  // Check if we're on the home page for special header styling
  const isHomePage = location.pathname === '/';

  return (
    <>
      {/* New Year Promo Banner - Only on home page */}
      {isHomePage && !promoExpired && (
        <div className="bg-gradient-to-r from-[#1a1a2e] via-[#16213e] to-[#1a1a2e] text-white py-3 px-4">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🎉</span>
              <span className="text-sm sm:text-base font-medium">
                Use coupon <span className="bg-white/20 px-2 py-0.5 rounded font-bold text-yellow-300">LAUNCHDAY</span> for an additional $10 bonus through the end of 2025
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm font-mono">
              <div className="flex items-center gap-1">
                <div className="bg-white/10 rounded px-2 py-1 min-w-[2.5rem] text-center">
                  <span className="text-lg font-bold">{countdown.days}</span>
                  <span className="text-[10px] block -mt-1 text-white/70">days</span>
                </div>
                <span className="text-white/50">:</span>
                <div className="bg-white/10 rounded px-2 py-1 min-w-[2.5rem] text-center">
                  <span className="text-lg font-bold">{String(countdown.hours).padStart(2, '0')}</span>
                  <span className="text-[10px] block -mt-1 text-white/70">hrs</span>
                </div>
                <span className="text-white/50">:</span>
                <div className="bg-white/10 rounded px-2 py-1 min-w-[2.5rem] text-center">
                  <span className="text-lg font-bold">{String(countdown.minutes).padStart(2, '0')}</span>
                  <span className="text-[10px] block -mt-1 text-white/70">min</span>
                </div>
                <span className="text-white/50">:</span>
                <div className="bg-white/10 rounded px-2 py-1 min-w-[2.5rem] text-center">
                  <span className="text-lg font-bold">{String(countdown.seconds).padStart(2, '0')}</span>
                  <span className="text-[10px] block -mt-1 text-white/70">sec</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <header
        className={`site-header transition-colors duration-200 ${
          isHomePage
            ? 'shadow-none'
            : 'bg-white dark:bg-gray-900 shadow-sm'
        }`}
        style={isHomePage ? { backgroundColor: '#216869' } : {}}
      >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Navbar fluid className="py-2 !bg-transparent dark:!bg-transparent">
          <Navbar.Brand as={Link} to="/">
            <div className="flex items-center">
              <img
                src={isHomePage ? "/assets/images/brand/head_logo_wb_inverse.png" : "/assets/images/head_logo.svg"}
                alt="Jetogo"
                className={isHomePage ? "h-16" : "h-8"}
                style={{width: 'auto'}}
              />
            </div>
          </Navbar.Brand>
          
          <div className="flex items-center gap-3 md:order-2">
            {/* Plans Dropdown - Desktop Only */}
            <div className={`hidden md:block ${isHomePage ? '[&_svg]:text-white' : '[&_svg]:text-[#49a078] dark:[&_svg]:text-white'}`}>
              <Dropdown
                inline
                className={isHomePage ? "text-white" : "text-[#49a078] dark:text-white"}
                label={
                  <span
                    className={`font-medium flex items-center ${isHomePage ? "text-white" : "text-[#49a078] dark:text-white"}`}
                  >
                    Plans
                  </span>
                }
              >
                <Dropdown.Item as={Link} to="/landing/pay-as-you-go">
                  Pay as You Go
                </Dropdown.Item>
                <Dropdown.Item as={Link} to="/landing/subscription-plans">
                  Subscription Plans
                </Dropdown.Item>
                <Dropdown.Item as={Link} to="/landing/business-plans">
                  Business Plans
                </Dropdown.Item>
                <Dropdown.Item onClick={() => {
                  navigate('/', { replace: false });
                  setTimeout(() => {
                    const element = document.getElementById('comparison');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}>
                  Compare All Plans
                </Dropdown.Item>
              </Dropdown>
            </div>

            {/* Hide user menu on payment page - users should focus on completing payment */}
            {!isPaymentPage && (
              <>
                {authLoading ? (
                  <Button color="light" size="sm" className="hidden md:flex opacity-50" disabled>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading...
                  </Button>
                ) : user ? (
                  <Dropdown
                    arrowIcon={false}
                    inline
                    label={
                      effectiveAvatarData && effectiveAvatarData.type === 'emoji' ? (
                        <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-2xl border-2 border-blue-500 transition-colors duration-200 pt-0.5">
                          {effectiveAvatarData.avatar}
                        </div>
                      ) : effectiveAvatarData && effectiveAvatarData.type === 'google' ? (
                        <Avatar
                          alt={hasUserData ? userData.name : "User"}
                          img={effectiveAvatarData.avatar}
                          rounded
                          bordered
                          color="blue"
                          size="md"
                        />
                      ) : (
                        <Avatar
                          alt={hasUserData ? userData.name : "User"}
                          img="https://flowbite.com/docs/images/people/profile-picture-5.jpg"
                          rounded
                          bordered
                          color="blue"
                          size="md"
                        />
                      )
                    }
                  >
                    <Dropdown.Header>
                      {userMenuHeader.showNameRow && (
                        <span className="block text-sm">{userMenuHeader.displayName}</span>
                      )}
                      <span className="block truncate text-sm font-medium">{userMenuHeader.displayEmail}</span>
                    </Dropdown.Header>
                    <Dropdown.Item as={Link} to={userData?.business_account_id ? "/business" : "/dashboard"}>Dashboard</Dropdown.Item>
                    <Dropdown.Item as={Link} to="/profile">Profile</Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={async () => {
                      await signOut();
                      navigate('/');
                    }}>
                      <div className="flex items-center">
                        <HiLogout className="mr-2" />
                        Sign out
                      </div>
                    </Dropdown.Item>
                  </Dropdown>
                ) : (
                  <Link to="/login">
                    <Button
                      size="sm"
                      className={`hidden md:flex items-center transition-all duration-300 ease-in-out ${
                        isHomePage
                          ? 'border border-white/50 bg-transparent hover:bg-white/10 text-white hover:text-white'
                          : 'border border-gray-300 dark:border-gray-600 bg-transparent hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      <HiLogin className="mr-1" />
                      Login
                    </Button>
                  </Link>
                )}
              </>
            )}
            {/* Hide mobile menu toggle on payment page - nothing to show */}
            {!isPaymentPage && (
              <Navbar.Toggle
                className={`md:hidden ${isHomePage ? '[&>svg]:text-white' : ''}`}
              />
            )}
          </div>
          
          <Navbar.Collapse className="md:flex-grow-0">
            <div className="flex flex-col md:flex-row md:space-x-6 md:items-center">
              {/* Plans dropdown for mobile only */}
              <div className={`md:hidden ${isHomePage ? '[&_svg]:text-white' : '[&_svg]:text-[#49a078] dark:[&_svg]:text-white'}`}>
                <Dropdown
                  inline
                  className={isHomePage ? "text-white" : "text-[#49a078] dark:text-white"}
                  label={
                    <span
                      className={`font-medium flex items-center ${isHomePage ? "text-white" : "text-[#49a078] dark:text-white"}`}
                    >
                      Plans
                    </span>
                  }
                >
                  <Dropdown.Item as={Link} to="/landing/pay-as-you-go">
                    Pay as You Go
                  </Dropdown.Item>
                  <Dropdown.Item as={Link} to="/landing/subscription-plans">
                    Subscription Plans
                  </Dropdown.Item>
                  <Dropdown.Item as={Link} to="/landing/business-plans">
                    Business Plans
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => {
                    navigate('/', { replace: false });
                    setTimeout(() => {
                      const element = document.getElementById('comparison');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }, 100);
                  }}>
                    Compare All Plans
                  </Dropdown.Item>
                </Dropdown>
              </div>

              {/* Hide user menu on payment page (mobile) */}
              {!isPaymentPage && (
                <div className="md:hidden mt-4 flex flex-col space-y-2">
                  {authLoading ? (
                    <Button color="light" className="w-full opacity-50" disabled>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading...
                    </Button>
                  ) : user ? (
                    <>
                      <Link to={userData?.business_account_id ? "/business" : "/dashboard"} className="w-full">
                        <Button color="primary" className="w-full">
                          Go to Dashboard
                        </Button>
                      </Link>
                      <Button
                        color="light"
                        className="w-full flex items-center justify-center"
                        onClick={async () => {
                          await signOut();
                          navigate('/');
                        }}
                      >
                        <HiLogout className="mr-2" />
                        Sign out
                      </Button>
                    </>
                  ) : (
                    <Link to="/login" className="w-full">
                      <Button color="light" className="w-full flex items-center justify-center">
                        <HiLogin className="mr-2" />
                        Login
                      </Button>
                    </Link>
                  )}
                </div>
              )}
            </div>
          </Navbar.Collapse>
        </Navbar>
      </div>
    </header>
    </>
  );
};

export default PublicHeader;